import { TIMEOUT_TIME_MAX_DIGITS } from '../constants/timeout'
import { readClockSeconds as readSeconds, toClockTime } from './periodClock'

/** The time field holds minutes and seconds in four digits, so it stops short of 100 minutes. */
const TIME_FIELD_LIMIT_SECONDS = 100 * 60

const twoDigits = (value: number): string =>
  value < 10 ? `0${value}` : `${value}`

/**
 * A clock in the one form timeouts are stored in, `HH:MM:SS`, or `null` when
 * the text is not a clock. It is read the way the API reads it: `MM:SS` or
 * `HH:MM:SS`, seconds no higher than 59, minutes carried over into hours.
 *
 * The app has stamped events with "Invalid Date" and "00:undefined" before
 * now. Nothing that is not a clock may be sent as one, or shown as one.
 */
export const toTimeoutClock = (clock?: string | null): string | null =>
  toClockTime(clock)

/**
 * Whether a clock is inside a period of the given length (milliseconds). A
 * period with no length has nothing to check against. An unreadable clock
 * never fits.
 */
export const fitsWithinPeriod = (
  clock?: string | null,
  periodDuration?: number | null
): boolean => {
  const seconds = readSeconds(clock)
  if (seconds === null) {
    return false
  }
  return !periodDuration || seconds * 1000 <= periodDuration
}

/**
 * A stored clock as the timelines show it: without the hours while they are
 * zero (`00:19:50` reads `19:50`). Empty when it is not a clock.
 */
export const toDisplayClock = (clock?: string | null): string => {
  const stored = toTimeoutClock(clock)
  if (stored === null) {
    return ''
  }
  return stored.indexOf('00:') === 0 ? stored.slice(3) : stored
}

/**
 * What was typed into the time field, as the digits it holds: digits only, and
 * no more than the field takes.
 */
export const readTimeFieldDigits = (text?: string | null): string =>
  typeof text === 'string'
    ? text.replace(/\D/g, '').slice(0, TIMEOUT_TIME_MAX_DIGITS)
    : ''

/**
 * The digits of the time field as the clock they stand for. They fill from the
 * right, like every scoring time screen: `7` is `00:07`, `507` is `05:07`,
 * `1950` is `19:50`. Empty while nothing is typed.
 */
export const formatTimeFieldDigits = (digits?: string | null): string => {
  const typed = readTimeFieldDigits(digits)
  if (typed === '') {
    return ''
  }
  const filled = `${'0000'.slice(typed.length)}${typed}`
  return `${filled.slice(0, 2)}:${filled.slice(2)}`
}

/**
 * A stored clock as the digits the time field holds (`00:19:50` is `1950`,
 * `00:05:07` is `507`, `01:05:00` is `6500`), for a timeout being edited.
 * Empty when it is not a clock, or is one the field cannot hold.
 *
 * The time screens' own converter (`sanitizeCurrentTime`) is not used for this:
 * it misreads any clock that has hours in it.
 */
export const toTimeFieldDigits = (clock?: string | null): string => {
  const seconds = readSeconds(clock)
  if (seconds === null || seconds >= TIME_FIELD_LIMIT_SECONDS) {
    return ''
  }
  const minutes = Math.floor(seconds / 60)
  return minutes === 0 ? `${seconds}` : `${minutes}${twoDigits(seconds % 60)}`
}

export interface TimeoutClockSources {
  /** The timeout goes into the period being played. */
  isPeriodInPlay: boolean
  /** The chosen period's length, in milliseconds. */
  periodDuration?: number | null
  /**
   * The running match clocks, where the app keeps one (soccer), most exact
   * first: with stoppage time, then without. `MM:SS`.
   */
  runningClocks?: ReadonlyArray<string | null | undefined>
  /** The last clock the app knows for the period in play (`game.currentPeriodTime`, `M:SS`). */
  lastKnownClock?: string | null
  /** The chosen period's own clock (`period.clock_time`, `HH:MM:SS`). */
  periodClock?: string | null
}

/**
 * The clock to stamp a timeout with when the scorer is not asked for one, or
 * `null` when the app has none to give (the API then uses the period's own).
 *
 * In the period being played it is the clock the screen already stamps its
 * other events with: the running match clock where there is one, else the
 * period's last known clock. A timeout added to any other period takes that
 * period's own clock. A clock that is longer than the period is never used:
 * the API would refuse the timeout over it.
 */
export const stampTimeoutClock = ({
  isPeriodInPlay,
  periodDuration,
  runningClocks = [],
  lastKnownClock,
  periodClock,
}: TimeoutClockSources): string | null => {
  const candidates = isPeriodInPlay
    ? [...runningClocks, lastKnownClock, periodClock]
    : [periodClock]
  const usable = candidates.find((candidate) =>
    fitsWithinPeriod(candidate, periodDuration)
  )
  return toTimeoutClock(usable)
}

/**
 * The digits a new timeout's time field starts with: the running match clock,
 * where the app keeps one and the timeout goes into the period being played.
 * Empty otherwise, and the scorer types the time. A clock longer than the
 * period is never offered: the field would refuse it straight away.
 */
export const prefillTimeFieldDigits = ({
  isPeriodInPlay,
  periodDuration,
  runningClocks = [],
}: Pick<
  TimeoutClockSources,
  'isPeriodInPlay' | 'periodDuration' | 'runningClocks'
>): string => {
  if (!isPeriodInPlay) {
    return ''
  }
  const usable = runningClocks.find((candidate) =>
    fitsWithinPeriod(candidate, periodDuration)
  )
  return toTimeFieldDigits(usable)
}

/**
 * The digits of the time field as the clock to store, or `null` while they are
 * not a time (`197` would be one minute and ninety-seven seconds).
 */
export const timeFieldDigitsToClock = (digits?: string | null): string | null =>
  toTimeoutClock(formatTimeFieldDigits(digits))

/** What is wrong with the digits of the time field: not a time, or past the end of the period. */
export type TimeFieldProblem = 'notATime' | 'outsidePeriod' | null

/**
 * The time field's rule: a real time (seconds no higher than 59) inside the
 * period the timeout goes into. The minutes run on past the hour as far as the
 * period does: in a 90-minute period `6500` is 65:00 and is a time; in a
 * 20-minute period it is past the end. A period with no length has nothing to
 * check against. Nothing typed is not a problem yet: Confirm stays off until
 * there is a time.
 */
export const checkTimeFieldDigits = (
  digits: string | null | undefined,
  periodDuration?: number | null
): TimeFieldProblem => {
  const typed = readTimeFieldDigits(digits)
  if (typed === '') {
    return null
  }
  const clock = timeFieldDigitsToClock(typed)
  if (clock === null) {
    return 'notATime'
  }
  return fitsWithinPeriod(clock, periodDuration) ? null : 'outsidePeriod'
}
